import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { useQuery, useMutation } from '@tanstack/react-query';
import { plantsApi, suppliersApi, gplOrdersApi } from '../../services/api';

const CreateOrderModal = ({ 
  open, 
  onClose, 
  onSuccess
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    plant_id: '',
    contract_id: '',
    tank_id: '',
    supplier_id: '',
    quantity_liters: '',
    expected_delivery_date: null,
    priority: 'normal',
    notes: '',
    send_email: false
  });

  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Carica lista impianti
  const plantsQuery = useQuery({
    queryKey: ['plants-for-order'],
    queryFn: async () => {
      const response = await plantsApi.getAll({ limit: 1000, attivo: true });
      return response.data?.data || response.data || [];
    },
    enabled: open
  });

  // Carica lista fornitori
  const suppliersQuery = useQuery({
    queryKey: ['suppliers-for-order'],
    queryFn: async () => {
      const response = await suppliersApi.getAll({ limit: 100 });
      return response.data?.data || response.data || [];
    },
    enabled: open
  });

  // Mutation per creare ordine
  const createOrderMutation = useMutation({
    mutationFn: (data) => gplOrdersApi.create(data),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Errore creazione ordine:', error);
    }
  });

  const plants = plantsQuery.data || [];
  const suppliers = suppliersQuery.data || [];

  useEffect(() => {
    if (!open) {
      // Reset form quando si chiude
      setFormData({
        plant_id: '',
        contract_id: '',
        tank_id: '',
        supplier_id: '',
        quantity_liters: '',
        expected_delivery_date: null,
        priority: 'normal',
        notes: '',
        send_email: false
      });
      setSelectedPlant(null);
      setSelectedSupplier(null);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePlantChange = (event, newValue) => {
    setSelectedPlant(newValue);
    setFormData(prev => ({
      ...prev,
      plant_id: newValue?.id || '',
      contract_id: newValue?.contract_id || '',
      tank_id: newValue?.tank_id || ''
    }));
  };

  const handleSupplierChange = (event, newValue) => {
    setSelectedSupplier(newValue);
    setFormData(prev => ({
      ...prev,
      supplier_id: newValue?.id || ''
    }));
  };

  const handleSubmit = () => {
    if (!formData.plant_id || !formData.quantity_liters) {
      return;
    }

    createOrderMutation.mutate({
      ...formData,
      expected_delivery_date: formData.expected_delivery_date 
        ? formData.expected_delivery_date.toISOString().split('T')[0]
        : null
    });
  };

  const getPlantLabel = (plant) => {
    if (!plant) return '';
    const parts = [];
    if (plant.plant_code) parts.push(plant.plant_code);
    if (plant.nome) parts.push(plant.nome);
    if (plant.indirizzo) parts.push(plant.indirizzo);
    if (plant.citta) parts.push(plant.citta);
    return parts.join(' - ') || `Impianto ${plant.id}`;
  };

  const getSupplierLabel = (supplier) => {
    if (!supplier) return '';
    return supplier.company_name || supplier.nome || `Fornitore ${supplier.id}`;
  };

  const isLoading = plantsQuery.isLoading || suppliersQuery.isLoading || createOrderMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Nuovo Ordine GPL</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            
            {/* Alert se dati in caricamento */}
            {(plantsQuery.isLoading || suppliersQuery.isLoading) && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<CircularProgress size={20} />}>
                  Caricamento impianti e fornitori...
                </Alert>
              </Grid>
            )}

            {/* Impianto */}
            <Grid item xs={12}>
              <Autocomplete
                options={plants}
                getOptionLabel={getPlantLabel}
                value={selectedPlant}
                onChange={handlePlantChange}
                loading={plantsQuery.isLoading}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option => 
                    (option.plant_code || '').toLowerCase().includes(searchTerm) ||
                    (option.delivery_code || '').toLowerCase().includes(searchTerm) ||
                    (option.nome || '').toLowerCase().includes(searchTerm) ||
                    (option.indirizzo || '').toLowerCase().includes(searchTerm) ||
                    (option.citta || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Impianto *" 
                    placeholder="Cerca per codice, nome o località..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {plantsQuery.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {option.plant_code || option.delivery_code || `#${option.id}`}
                        </Typography>
                        <Typography variant="body1">
                          {option.nome || 'N/D'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {option.indirizzo}, {option.citta} {option.provincia && `(${option.provincia})`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {option.tank_capacity && (
                          <Chip 
                            label={`Capacità: ${Math.round(option.tank_capacity).toLocaleString('it-IT')}L`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                        {(option.estimated_level_liters !== undefined && option.estimated_level_liters !== null) ? (
                          <Chip 
                            label={`Stima oggi: ${Math.round(option.estimated_level_liters).toLocaleString('it-IT')}L (${Math.round(option.estimated_level_percent || 0)}%)`} 
                            size="small" 
                            color={option.estimated_level_percent < 20 ? 'error' : option.estimated_level_percent < 40 ? 'warning' : 'success'}
                          />
                        ) : option.current_level_liters !== undefined && option.current_level_liters !== null && (
                          <Chip 
                            label={`Livello: ${Math.round(option.current_level_liters).toLocaleString('it-IT')}L`} 
                            size="small" 
                            color={option.current_level_liters < 500 ? 'error' : 'success'}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            {/* Info impianto selezionato */}
            {selectedPlant && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Capacità serbatoio:</strong> {Math.round(selectedPlant.tank_capacity || selectedPlant.capacita_serbatoio || 0).toLocaleString('it-IT')} L | 
                    <strong> Stima oggi:</strong> {selectedPlant.estimated_level_liters !== null && selectedPlant.estimated_level_liters !== undefined 
                      ? `${Math.round(selectedPlant.estimated_level_liters).toLocaleString('it-IT')} L (${Math.round(selectedPlant.estimated_level_percent || 0)}%)`
                      : (selectedPlant.current_level_liters !== null && selectedPlant.current_level_liters !== undefined 
                        ? `${Math.round(selectedPlant.current_level_liters).toLocaleString('it-IT')} L` 
                        : 'N/D')}
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Fornitore */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={suppliers}
                getOptionLabel={getSupplierLabel}
                value={selectedSupplier}
                onChange={handleSupplierChange}
                loading={suppliersQuery.isLoading}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Fornitore" 
                    placeholder="Seleziona fornitore..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {suppliersQuery.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1">
                        {option.company_name}
                      </Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                      {option.phone && (
                        <Typography variant="caption" color="text.secondary">
                          Tel: {option.phone}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>

            {/* Quantità */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quantità *"
                name="quantity_liters"
                type="number"
                value={formData.quantity_liters}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Litri</InputAdornment>
                }}
              />
            </Grid>

            {/* Data prevista consegna */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data prevista consegna"
                value={formData.expected_delivery_date}
                onChange={(date) => setFormData(prev => ({ ...prev, expected_delivery_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Priorità */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priorità</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priorità"
                >
                  <MenuItem value="normal">Normale</MenuItem>
                  <MenuItem value="urgent">Urgente</MenuItem>
                  <MenuItem value="emergency">Emergenza</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Invia email */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.send_email}
                    onChange={handleChange}
                    name="send_email"
                    disabled={!formData.supplier_id}
                  />
                }
                label="Invia email al fornitore"
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            {/* Errore */}
            {createOrderMutation.isError && (
              <Grid item xs={12}>
                <Alert severity="error">
                  Errore nella creazione dell'ordine: {createOrderMutation.error?.response?.data?.message || createOrderMutation.error?.message || 'Errore sconosciuto'}
                </Alert>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isLoading || !formData.plant_id || !formData.quantity_liters}
          startIcon={createOrderMutation.isPending && <CircularProgress size={20} />}
        >
          Crea Ordine
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrderModal;
